/**
 * Emitir una devolución de una factura que NO está en esta aplicación.
 *
 * POR QUÉ EXISTE
 * --------------
 * Angel, 11-sep-2026: "tenemos que hacer una factura de devolución de una
 * factura realizada con la anterior aplicación, por lo tanto debes dejar
 * hacerla a mano y usar la numeración que tenemos ya hecha".
 *
 * El diálogo de rectificativa no vale para esto: arranca de una factura de
 * aquí, con sus líneas y sus importes ya guardados. Si la original se emitió
 * con la aplicación anterior, no hay nada de donde copiar. Se escribe.
 *
 * QUÉ NO CAMBIA
 * -------------
 * El número sale de la serie AB -- que es la numeración antigua, continuada
 * aquí desde el 213 --, el motivo es obligatorio y los importes acaban en
 * negativo. Es el mismo documento fiscal por otra puerta.
 *
 * LA REGLA DEL SIGNO
 * ------------------
 * Los importes se escriben en POSITIVO: lo que se cobró. El menos lo pone el
 * servidor. Pedirle el signo a quien teclea es pedirle que se equivoque, y un
 * menos olvidado no es una devolución: es una factura más, con número de
 * abono. El resumen de abajo ya lo enseña en negativo para que se vea antes de
 * pulsar.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ClientSearchSelect } from '@/components/ClientSearchSelect';
import { toast } from 'sonner';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { mensajeDeError } from '@/lib/errores';
import {
  cuerpoDeDevolucion, motivoParaNoEmitir, regimenDelCliente, totalDeLaDevolucion,
} from '@/lib/devolucion-manual';

const euros = (n) => `${Math.abs(n ?? 0).toFixed(2)} €`;

const LINEA_VACIA = { description: '', quantity: 1, unit_price: '', discount: 0, tax_rate: '' };

export function DialogoDevolucionManual({ open, onOpenChange, onHecho }) {
  const [cliente, setCliente] = useState(null);
  const [form, setForm] = useState({
    customer_id: '',
    factura_original_numero: '',
    factura_original_fecha: '',
    motivo: '',
    payment_method: '',
    notes: '',
  });
  const [lineas, setLineas] = useState([{ ...LINEA_VACIA }]);
  const [emitiendo, setEmitiendo] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCliente(null);
    setForm({
      customer_id: '', factura_original_numero: '', factura_original_fecha: '',
      motivo: '', payment_method: '', notes: '',
    });
    setLineas([{ ...LINEA_VACIA }]);
  }, [open]);

  const { regimen } = useMemo(() => regimenDelCliente(cliente), [cliente]);
  const totales = useMemo(() => totalDeLaDevolucion(lineas, cliente), [lineas, cliente]);

  const cambiarLinea = (i, campo, valor) => setLineas((prev) =>
    prev.map((l, n) => (n === i ? { ...l, [campo]: valor } : l)));
  const quitarLinea = (i) => setLineas((prev) =>
    (prev.length === 1 ? prev : prev.filter((_, n) => n !== i)));

  const formulario = { ...form, line_items: lineas };
  // Se pide un total > 0 aparte del resto: es la comprobación que hace el
  // servidor justo antes de gastar un número de la serie.
  const pega = motivoParaNoEmitir(formulario)
    || (totales.total <= 0 ? 'La devolución tiene que sumar más de 0 €.' : null);

  const emitir = async () => {
    setEmitiendo(true);
    try {
      const { data } = await axiosInstance.post(
        '/invoices/returns', cuerpoDeDevolucion(formulario));
      toast.success(`Devolución ${data.numero} emitida por ${euros(data.total)}`);
      onOpenChange(false);
      onHecho?.(data);
    } catch (e) {
      toast.error(mensajeDeError(e));
    } finally {
      setEmitiendo(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto"
                     data-testid="dialogo-devolucion-manual">
        <DialogHeader>
          <DialogTitle>Nueva devolución</DialogTitle>
          <DialogDescription>
            De una factura emitida con la aplicación anterior, que no está aquí
          </DialogDescription>
        </DialogHeader>

        {/* Mismo aviso que la rectificativa, y por lo mismo: esto no es un
            borrador que se pueda borrar luego. */}
        <div className="border border-amber-200 bg-amber-50 rounded p-3">
          <p className="text-amber-900 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Esto emite un documento fiscal con su propio número de serie
              (AB26-…). No se puede anular: para deshacerlo habría que emitir
              otro documento.
            </span>
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>Cliente al que se le devuelve *</Label>
          <ClientSearchSelect
            label={null}
            value={form.customer_id}
            onValueChange={(id, ficha) => {
              setForm((f) => ({
                ...f,
                customer_id: id || '',
                // Viene puesta la de su ficha: se devuelve por donde se cobra.
                // Se puede cambiar, que una devolución a veces va por
                // transferencia aunque su cuota se le gire.
                payment_method: ficha?.payment_method || f.payment_method,
              }));
              setCliente(ficha || null);
            }}
          />
          {regimen === 'NO_SUJETO' && (
            <p className="text-xs text-blue-700">
              Cliente fuera del territorio del IVA: la devolución sale sin impuesto.
            </p>
          )}
        </div>

        {/* Los datos de la factura rectificada. Se escriben porque no está
            aquí: es un documento de la aplicación anterior, no algo que se
            pueda enlazar. Los dos son obligatorios -- una rectificativa tiene
            que decir a qué factura corrige y de qué fecha. */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="factura-original">Nº de la factura que se rectifica *</Label>
            <Input
              id="factura-original"
              value={form.factura_original_numero}
              onChange={(e) => setForm({ ...form, factura_original_numero: e.target.value })}
              placeholder="El que tenga en la aplicación anterior"
              data-testid="factura-original-numero"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fecha-original">Fecha de esa factura *</Label>
            <Input
              id="fecha-original" type="date"
              value={form.factura_original_fecha}
              onChange={(e) => setForm({ ...form, factura_original_fecha: e.target.value })}
              data-testid="factura-original-fecha"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label>Qué se devuelve</Label>
            <span className="text-xs text-slate-500">Importes en positivo</span>
          </div>
          <div className="border rounded divide-y">
            {lineas.map((linea, i) => (
              <div key={i} className="p-3 space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={linea.description}
                    onChange={(e) => cambiarLinea(i, 'description', e.target.value)}
                    placeholder="Concepto"
                    data-testid={`linea-${i}-descripcion`}
                  />
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => quitarLinea(i)}
                    disabled={lineas.length === 1}
                    aria-label="Quitar concepto"
                  >
                    <Trash2 className="w-4 h-4 text-slate-400" />
                  </Button>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs text-slate-500">Cantidad</Label>
                    <Input
                      type="number" min="0" step="any" value={linea.quantity}
                      onChange={(e) => cambiarLinea(i, 'quantity', e.target.value)}
                      data-testid={`linea-${i}-cantidad`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Precio</Label>
                    <Input
                      type="number" min="0" step="0.01" value={linea.unit_price}
                      onChange={(e) => cambiarLinea(i, 'unit_price', e.target.value)}
                      data-testid={`linea-${i}-precio`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Dto. %</Label>
                    <Input
                      type="number" min="0" max="100" step="any" value={linea.discount}
                      onChange={(e) => cambiarLinea(i, 'discount', e.target.value)}
                    />
                  </div>
                  <div>
                    {/* Vacío significa "el que le toque al cliente", no 0. A un
                        cliente no sujeto se le queda en 0 aunque aquí se
                        escriba otra cosa: manda su régimen. */}
                    <Label className="text-xs text-slate-500">% impuesto</Label>
                    <Input
                      type="number" min="0" max="100" step="any" value={linea.tax_rate}
                      onChange={(e) => cambiarLinea(i, 'tax_rate', e.target.value)}
                      placeholder={regimen === 'NO_SUJETO' ? '0' : 'del cliente'}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => setLineas((prev) => [...prev, { ...LINEA_VACIA }])}
            data-testid="anadir-concepto"
          >
            <Plus className="w-4 h-4 mr-1" /> Añadir concepto
          </Button>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="motivo-devolucion">Motivo de la devolución *</Label>
          <Textarea
            id="motivo-devolucion" rows={2} value={form.motivo}
            onChange={(e) => setForm({ ...form, motivo: e.target.value })}
            placeholder="Ej: se le cobró un mes de más al migrar de la aplicación anterior"
            data-testid="motivo-devolucion"
          />
          <p className="text-xs text-slate-500">
            Sale impreso en la factura. Es obligatorio: una rectificativa tiene
            que decir qué corrige y por qué.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label>Cómo se devuelve el dinero *</Label>
          <Select
            value={form.payment_method || ''}
            onValueChange={(value) => setForm({ ...form, payment_method: value })}
          >
            <SelectTrigger data-testid="forma-pago-devolucion">
              <SelectValue placeholder="Elige cómo se devuelve — obligatorio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sepa">🏦 Recibo Domiciliado (SEPA)</SelectItem>
              <SelectItem value="transferencia">💸 Transferencia Bancaria</SelectItem>
              <SelectItem value="programado">📅 Pago programado por el cliente</SelectItem>
              <SelectItem value="efectivo">💵 Efectivo</SelectItem>
              <SelectItem value="tarjeta">💳 Tarjeta</SelectItem>
              <SelectItem value="link">🔗 Link de Pago Online</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border-t pt-3 flex items-end justify-between gap-4">
          <p className="text-xs text-amber-700">{pega}</p>
          <div className="text-right text-sm space-y-0.5 shrink-0">
            <div className="text-slate-600">Base: −{euros(totales.base)}</div>
            <div className="text-slate-600">Impuesto: −{euros(totales.cuota)}</div>
            <div className="text-lg font-bold text-[#9C0F17]" data-testid="total-devolucion">
              A devolver: −{euros(totales.total)}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={emitir} disabled={Boolean(pega) || emitiendo}
                  className="bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                  data-testid="emitir-devolucion">
            {emitiendo ? 'Emitiendo...' : 'Emitir devolución'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DialogoDevolucionManual;
